import React, {useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Button, Colors, Icon, Intent, Position} from '@blueprintjs/core';
import {Tooltip2} from '@blueprintjs/popover2';
import {IconNames} from '@blueprintjs/icons';

import {ApiStatus, Favorite, FavoriteType} from '../../models';
import {IState} from '../../store/reducers';
import {
  createFavorite,
  deleteFavorite,
} from '../../store/actions/favoriteActions';

interface ToggleFavoriteProps {
  uid?: string;
  id?: number;
  type: FavoriteType;
  favorite?: Favorite;
  buttonProps?: any;
}

const FINISH_STATUS = [ApiStatus.LOADED, ApiStatus.FAILED];

const ToggleFavorite = ({
  uid,
  id,
  type,
  buttonProps = undefined,
}: ToggleFavoriteProps) => {
  /*
   * TODO - extract loading logic into a hook
   */
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const favorites = useSelector((state: IState) => state.favorite.results); // TODO - improve
  const creatingStatus = useSelector(
    (state: IState) => state.favorite.creatingStatus
  );
  const deletingStatus = useSelector(
    (state: IState) => state.favorite.deletingStatus
  );
  const dispatch = useDispatch();
  const favorite = favorites.find((f: Favorite) => f.class_name === type && f.item?.id === id) || null;
  const isFavorite = !!favorite;

  useEffect(() => {
    if (creating && creatingStatus && FINISH_STATUS.includes(creatingStatus)) {
      setCreating(false);
    }
  }, [creating, creatingStatus]);

  useEffect(() => {
    if (deleting && deletingStatus && FINISH_STATUS.includes(deletingStatus)) {
      setDeleting(false);
    }
  }, [deleting, deletingStatus]);

  const onClick = () => {
    if (creating || deleting) {
      return;
    }
    if (isFavorite) {
      dispatch(deleteFavorite(favorite?.uid as string));
      setDeleting(true);
    } else {
      const favorite = {
        class_name: type,
        obj_id: id as number, // @TODO - change this to uid after adding support
      };
      dispatch(createFavorite(favorite));
      setCreating(true);
    }
  };

  return (
    <Tooltip2
      className="toggle-favorite-tooltip"
      content={isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
      position={Position.TOP}
      hoverOpenDelay={1000}
    >
      <Button
        className="toggle-favorite-button"
        intent={isFavorite ? Intent.WARNING : Intent.NONE}
        minimal={true}
        icon={
          isFavorite ? <Icon icon={IconNames.STAR} color={Colors.GOLD4} /> : IconNames.STAR_EMPTY
        }
        onClick={onClick}
        loading={creating || deleting}
        {...buttonProps || {}}
      />
    </Tooltip2>
  );
};

export default ToggleFavorite;
